import { defineStore } from 'pinia';
import { computed, markRaw, type Raw, reactive, type Reactive, ref } from 'vue';
import { stopAll, type StopHandle, taggedLogger } from '@/util';
import { getCase } from '@/api/case';
import { type ApiStudyId, studyIdFromUrl } from '@/api/study/study';
import type { Url } from '@/domain/types';
import { XRAY_2D_3D_VIEW_IDS, type XRay2D3DViewId } from '@/api/xrays';
import { client } from '@/api/http';
import { HttpStatusCode } from 'axios';
import {
    fullViewCameraParams,
    xraySceneState,
    type XRaySceneState,
} from '@/views/dicoms/xrays/xraySceneState';
import type { XRayViewportId } from '@/views/dicoms/xrays/xrayScene';
import { textureState, type TextureState, updateTexture } from '@/lib/threeScene/textures/texture';
import { MeshBasicMaterial, type Texture, type Vector3 } from 'three';
import { watchImmediate } from '@vueuse/core';
import { zero3 } from '@/domain/geometry/vector3';
import type { ElementSize } from '@/util/observeElementSize';

const _log = taggedLogger('xrays-view');
const _INITIAL_VIEW_ID: XRay2D3DViewId = 'ap';

export type XRayViewState = {
    readonly viewId: XRay2D3DViewId;
    imageSize: { width: number; height: number } | null;
    imageTexture: TextureState;
    imageMaterial: Raw<MeshBasicMaterial>;
    cameraTarget: Vector3;
    cameraZoom: number;
};

type _XRayViewStateMap = Record<XRay2D3DViewId, XRayViewState>;
type _XRaySceneStateMap = Record<XRayViewportId, XRaySceneState>;

export const useXRaysStore = defineStore('xrays-view', () => {
    let _stop: StopHandle = () => {
    };
    const mainViewId = ref<XRay2D3DViewId>(_INITIAL_VIEW_ID);

    const _viewStates: _XRayViewStateMap = reactive({
        'ap': _makeViewState('ap'),
        'left-oblique': _makeViewState('left-oblique'),
        'right-oblique': _makeViewState('right-oblique')
    });

    const _sceneStates: _XRaySceneStateMap = reactive({
        'main': xraySceneState('main'),
        'ap-overview': xraySceneState('ap-overview'),
        'left-oblique-overview': xraySceneState('left-oblique-overview'),
        'right-oblique-overview': xraySceneState('right-oblique-overview')
    });

    function _reset() {
        for (const viewId of XRAY_2D_3D_VIEW_IDS) {
            _resetViewState(_viewStates[viewId]);
        }
        mainViewId.value = _INITIAL_VIEW_ID;
    }

    return {
        mainViewId,
        mainViewZoom: computed<number>({
            get: () => {
                const zoom = _viewStates[mainViewId.value].cameraZoom;
                const result = Math.log10(zoom)
                _log.info('Get zoom: %f (%f)', result, zoom)
                return result;
            },
            set: (value) => {
                const zoom = Math.pow(10, value)
                _log.info('Set zoom: %f (%f)', value, zoom)
                _viewStates[mainViewId.value].cameraZoom = zoom;
            },
        }),
        resetMainView: () => {
            const mainViewState = _viewStates[mainViewId.value];
            const imageSize = mainViewState.imageSize;
            const viewportSize = _sceneStates.main.rendering.viewportSize;
            if (imageSize === null || viewportSize === null) {
                _log.warning('Cannot reset main view: %s is null', imageSize === null ? 'imageSize' : 'viewportSize');
                return;
            }
            const { target, zoom } = fullViewCameraParams(imageSize, viewportSize);
            mainViewState.cameraTarget = target;
            mainViewState.cameraZoom = zoom;
        },
        viewState(viewId: XRay2D3DViewId): Reactive<XRayViewState> {
            return _viewStates[viewId];
        },
        sceneState(viewportId: XRayViewportId): Reactive<XRaySceneState> {
            return _sceneStates[viewportId];
        },
        loadCase: async (caseId: number): Promise<void> => {
            _stop();

            const controller = new AbortController();
            const signal = controller.signal;
            _stop = () => {
                controller.abort();
                _reset();
            };

            try {
                const case_ = await getCase(caseId, { signal });
                if (!case_.study) {
                    _log.error('Could not get study-id for case %d', caseId);
                    return;
                }

                const viewportSize = _sceneStates.main.rendering.viewportSize;
                if (viewportSize === null) {
                    _log.error('Viewport size is null when x-ray case is loaded');
                    return;
                }

                const studyId = studyIdFromUrl(case_.study);

                for (const viewId of XRAY_2D_3D_VIEW_IDS) {
                    const imageInfo = await _getXRayInfo(studyId, viewId, signal);
                    const viewState = _viewStates[viewId];
                    if (imageInfo === null) {
                        _log.error('Failed to get image-info for xray view %s', viewId);
                        return;
                    } else {
                        _initializeViewState(viewState, imageInfo, viewportSize);
                    }
                }
            } catch (error) {
                if (controller.signal.aborted) {
                    _log.info('Case loading for xray-views aborted');
                    return;
                } else {
                    throw error;
                }
            }
            _stop = stopAll(
                ...XRAY_2D_3D_VIEW_IDS.map((state) => _updateViewState(_viewStates[state])),
                _reset,
            );
        },
        stop() {
            _stop();
        },
    };
});

type _XRayImageInfo = Readonly<{
    /** Url of the image */
    url: Url;

    size: {
        /** Width of the image, in pixels */
        width: number;

        /** Height of the image, in pixels */
        height: number;
    };
}>;

async function _getXRayInfo(
    studyId: ApiStudyId,
    viewId: XRay2D3DViewId,
    signal: AbortSignal,
): Promise<_XRayImageInfo | null> {
    const url = `project/${studyId.case}/study/${studyId.study}/xray/${viewId}`;
    const { status, data } = await client.get<{ size: { width: number; height: number } }>(url, {
        signal,
    });
    if (status === HttpStatusCode.Ok) {
        return {
            url: url,
            ...data,
        };
    }
    _log.warning('Failed to get xray image info for view %s: %s', viewId, status);
    return null;
}

function _makeViewState(viewId: XRay2D3DViewId): XRayViewState {
    return {
        viewId,
        imageSize: null,
        imageTexture: textureState(),
        imageMaterial: markRaw(new MeshBasicMaterial()),
        cameraTarget: zero3(),
        cameraZoom: 1,
    };
}

function _resetViewState(state: XRayViewState) {
    state.imageSize = null;
    state.imageTexture.source = null;
    state.cameraTarget = zero3();
    state.cameraZoom = 1;
}

function _initializeViewState(
    state: XRayViewState,
    { url, size: imageSize }: _XRayImageInfo,
    viewportSize: ElementSize,
) {
    state.imageTexture.source = url;
    state.imageSize = imageSize;
    const { target, zoom } = fullViewCameraParams(imageSize, viewportSize);
    state.cameraTarget = target;
    state.cameraZoom = zoom;
}

function _updateViewState(state: Reactive<XRayViewState>): StopHandle {
    return stopAll(
        updateTexture(state.imageTexture),
        watchImmediate(
            () => state.imageTexture.texture,
            (texture: Texture | null) => {
                state.imageMaterial.map = texture;
                state.imageMaterial.needsUpdate = true;
            },
        ),
        watchImmediate(
            () => state.imageTexture.sourceVersion,
            () => (state.imageMaterial.needsUpdate = true),
        ),
    );
}
